import { useState, useEffect } from "react";
import DashboardLayout from "../components/DashboardLayout";
import WorkflowIndicator from "../components/WorkflowIndicator";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import {
  ticketAPI,
  commentAPI,
  attachmentAPI,
  feedbackAPI,
  adminAPI,
} from "../services/api";
import { toast } from "sonner";
import {
  ArrowLeft,
  Clock,
  User,
  Tag,
  FileText,
  Download,
  MessageSquare,
  Star,
  CheckCircle,
  AlertCircle,
  Loader2,
  Send,
  AlertTriangle,
  UserPlus,
  Upload,
  Paperclip,
} from "lucide-react";
import { motion } from "framer-motion";

export default function TicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("IN_PROGRESS");
  const [isUpdating, setIsUpdating] = useState(false);
  const [resolutionNote, setResolutionNote] = useState("");
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [newComment, setNewComment] = useState("");
  const [comments, setComments] = useState<any[]>([]);
  const [showEscalateDialog, setShowEscalateDialog] = useState(false);
  const [showReassignDialog, setShowReassignDialog] = useState(false);
  const [technicians, setTechnicians] = useState<any[]>([]);
  const [selectedTechnicianId, setSelectedTechnicianId] = useState("");
  const [reassignReason, setReassignReason] = useState("");
  const [escalationReason, setEscalationReason] = useState("");
  const [loadingTechnicians, setLoadingTechnicians] = useState(false);
  const [attachments, setAttachments] = useState<any[]>([]);
  const [uploadingFiles, setUploadingFiles] = useState<string[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  const toApiStatus = (value: string) => value.toUpperCase().replace(/-/g, "_");

  const toUiStatus = (value: string) => value.toLowerCase().replace(/_/g, "-");

  useEffect(() => {
    if (id) {
      loadTicketData();
    }
  }, [id]);

  useEffect(() => {
    if (showReassignDialog) {
      loadTechnicians();
    }
  }, [showReassignDialog]);

  const loadTicketData = async () => {
    try {
      setLoading(true);
      const response = await ticketAPI.get(id!);

      if (response.success || response.data) {
        const ticketData = response.data || response;
        setTicket(ticketData);
        setStatus(toApiStatus(ticketData.status || "OPEN"));
        setResolutionNote(ticketData.resolutionNotes || "");

        if (ticketData.comments) {
          setComments(ticketData.comments);
        }

        loadAttachments();
      } else {
        toast.error("Ticket not found");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error("Failed to load ticket details");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const loadAttachments = async () => {
    try {
      const response = await attachmentAPI.list(id!);
      if (response.success || Array.isArray(response)) {
        const attachmentsList = Array.isArray(response)
          ? response
          : response.data || [];

        const normalizedAttachments = attachmentsList.map((att: any) => ({
          AttachmentID: att.AttachmentID || att.id || att.attachmentId,
          FileName: att.FileName || att.fileName || att.filename || att.name,
          FileSize: att.FileSize || att.fileSize || att.size || 0,
          DateUploaded:
            att.DateUploaded ||
            att.uploadedAt ||
            att.createdAt ||
            att.uploaded_at ||
            att.created_at,
          ...att,
        }));

        setAttachments(normalizedAttachments);
      }
    } catch (error) {}
  };

  const loadComments = async () => {
    try {
      const response = await commentAPI.list(id!);
      if (response.success || Array.isArray(response)) {
        const commentsList = Array.isArray(response)
          ? response
          : response.data || [];
        setComments(commentsList);
      }
    } catch (error) {}
  };

  const handleFileUpload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);
    const uploadPromises = fileArray.map(async (file) => {
      const fileId = `${file.name}-${Date.now()}`;
      setUploadingFiles((prev) => [...prev, fileId]);

      try {
        const response = await attachmentAPI.upload(id!, file);

        if (response.success || response.data) {
          toast.success(`Uploaded ${file.name}`);
          loadAttachments();
        } else {
          throw new Error("Upload failed");
        }
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      } finally {
        setUploadingFiles((prev) => prev.filter((f) => f !== fileId));
      }
    });

    await Promise.all(uploadPromises);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const handleDownload = async (attachmentId: string, fileName: string) => {
    try {
      const response = await attachmentAPI.download(id!, attachmentId);

      if (response.success) {
        toast.success(`Downloaded ${response.filename || fileName}`);
      }
    } catch (error: any) {
      toast.error(error.message || "Failed to download file");
      console.error("Download error:", error);
    }
  };

  const formatFileSize = (bytes: number | string) => {
    const numBytes = typeof bytes === "string" ? parseInt(bytes, 10) : bytes;
    if (isNaN(numBytes) || numBytes === 0) return "0 B";
    if (numBytes < 1024) return numBytes + " B";
    if (numBytes < 1024 * 1024) return (numBytes / 1024).toFixed(1) + " KB";
    return (numBytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const statusOptions = [
    { value: "OPEN", label: "Open", color: "bg-red-500/10 text-red-400" },
    {
      value: "IN_PROGRESS",
      label: "In Progress",
      color: "bg-blue-500/10 text-blue-400",
    },
    {
      value: "PENDING",
      label: "Pending",
      color: "bg-yellow-500/10 text-yellow-400",
    },
    {
      value: "RESOLVED",
      label: "Resolved",
      color: "bg-green-500/10 text-green-400",
    },
    {
      value: "CLOSED",
      label: "Closed",
      color: "bg-gray-500/10 text-gray-400",
    },
  ];

  const handleStatusUpdate = async () => {
    const selectedStatus = toApiStatus(status);

    if (
      (selectedStatus === "RESOLVED" || selectedStatus === "CLOSED") &&
      !resolutionNote.trim()
    ) {
      toast.error("Resolution note required");
      return;
    }

    setIsUpdating(true);
    try {
      await ticketAPI.updateStatus(id!, selectedStatus);

      if (selectedStatus === "RESOLVED" || selectedStatus === "CLOSED") {
        // Non-blocking secondary update so UI doesn't stay loading
        void ticketAPI
          .update(id!, {
            ResolutionNotes: resolutionNote,
          })
          .catch(() => {
            toast.info("Resolution note save is still processing");
          });
      }

      setStatus(selectedStatus);
      setTicket({ ...ticket, status: selectedStatus, Status: selectedStatus });
      toast.success("Ticket status updated", {
        description: `Status changed to ${selectedStatus}`,
      });
    } catch (error) {
      toast.error("Failed to update ticket");
    } finally {
      setIsUpdating(false);
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast.error("Comment cannot be empty");
      return;
    }

    setIsSubmittingComment(true);
    try {
      const response = await commentAPI.add(id!, {
        text: newComment,
        isInternal: false,
      });

      if (response?.success === false) {
        throw new Error(response?.message || "Failed to add comment");
      }

      const createdComment =
        response?.data?.comment ||
        response?.data?.data ||
        response?.data ||
        response?.comment ||
        null;

      toast.success("Comment added successfully");
      const submittedText = newComment;
      setNewComment("");

      // Optimistic update so users see comment instantly without reload
      if (createdComment && typeof createdComment === "object") {
        setComments((prev) => [...prev, createdComment]);
      } else {
        setComments((prev) => [
          ...prev,
          {
            id: `temp-${Date.now()}`,
            text: submittedText,
            createdAt: new Date().toISOString(),
            user: {
              fullName: user?.name || "You",
              role: user?.role || "user",
            },
            isInternal: false,
          },
        ]);
      }

      // Refresh comments in background to avoid blocking button state
      void loadComments();
    } catch (error) {
      toast.error("Failed to add comment");
    } finally {
      setIsSubmittingComment(false);
    }
  };

  const handleFeedbackSubmit = async () => {
    if (rating === 0) {
      toast.error("Please provide a rating");
      return;
    }

    setIsSubmittingFeedback(true);
    try {
      const response = await feedbackAPI.submit(
        id!,
        rating,
        feedback || undefined,
        false,
      );

      if (response?.success === false) {
        throw new Error(response?.message || "Feedback submission failed");
      }

      toast.success("Thank you for your feedback!", {
        description: "Your rating helps us improve our service",
      });

      setRating(0);
      setFeedback("");
    } catch (error) {
      toast.error("Failed to submit feedback");
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const handleEscalate = async () => {
    if (!escalationReason.trim()) {
      toast.error("Please provide a reason for escalation");
      return;
    }

    try {
      await ticketAPI.escalate(id!, escalationReason);

      setStatus("PENDING");
      setTicket({ ...ticket, status: "PENDING", Status: "PENDING" });

      toast.warning("Ticket Escalated", {
        description: "Escalated to senior technician for complex resolution",
      });

      toast.info("Notification Sent", {
        description: "Senior technician and user notified via email",
      });

      setEscalationReason("");
      setShowEscalateDialog(false);
    } catch (error) {
      toast.error("Failed to escalate ticket");
    }
  };

  const loadTechnicians = async () => {
    try {
      setLoadingTechnicians(true);
      const response = await adminAPI.technicianStats();

      if (response.success && response.data) {
        const techList = Array.isArray(response.data)
          ? response.data
          : response.data.technicians || [];
        setTechnicians(techList);
      }
    } catch (error) {
      toast.error("Failed to load technicians");
    } finally {
      setLoadingTechnicians(false);
    }
  };

  const handleReassign = async () => {
    if (!selectedTechnicianId) {
      toast.error("Please select a technician");
      return;
    }

    try {
      const selectedTech = technicians.find(
        (tech: any) =>
          (tech.technician?.id || tech.id) === selectedTechnicianId,
      );
      const techInfo = {
        id: selectedTechnicianId,
        fullName:
          selectedTech?.technician?.fullName ||
          selectedTech?.fullName ||
          "Assigned",
        email: selectedTech?.technician?.email || selectedTech?.email || "",
      };

      await ticketAPI.assign(id!, selectedTechnicianId);

      setTicket({
        ...ticket,
        assignedTo: techInfo,
        assignedTechnician: techInfo,
        assignedId: selectedTechnicianId,
      });

      toast.success("Ticket Reassigned", {
        description: `Reassigned to ${techInfo.fullName}`,
      });

      setSelectedTechnicianId("");
      setReassignReason("");
      setShowReassignDialog(false);
    } catch (error) {
      toast.error("Failed to reassign ticket");
    }
  };

  const handleResolveTicket = async () => {
    if (!resolutionNote.trim()) {
      toast.error("Resolution notes required");
      return;
    }

    setIsUpdating(true);
    try {
      await ticketAPI.update(id!, {
        status: "RESOLVED",
        resolutionNotes: resolutionNote,
      });

      toast.success("Ticket Resolved", {
        description: "Resolution details saved successfully",
      });

      toast.info("User Notified", {
        description: "Resolution notification sent via email",
      });

      setStatus("RESOLVED");

      setTicket((prev: any) => ({
        ...prev,
        status: "RESOLVED",
        resolvedAt: new Date().toISOString(),
        resolutionNotes: resolutionNote,
      }));

      setTimeout(() => {
        toast.info("Feedback Requested", {
          description: "Please rate this service and provide feedback",
          duration: 6000,
        });
      }, 1000);
    } catch (error) {
      toast.error("Failed to resolve ticket");
    } finally {
      setIsUpdating(false);
    }
  };

  const priorityColors = {
    LOW: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    MEDIUM: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
    HIGH: "bg-orange-500/10 text-orange-400 border-orange-500/30",
    CRITICAL: "bg-red-500/10 text-red-400 border-red-500/30",
  };

  const slaDeadline = ticket?.slaDeadline || ticket?.SLADeadline || null;
  const resolutionSlaStatus =
    ticket?.sla?.resolution?.status || ticket?.slaStatus || ticket?.sla?.status;
  const firstResponseStatus =
    ticket?.firstResponse?.status ||
    ticket?.sla?.firstResponse?.status ||
    ticket?.firstResponseStatus ||
    null;
  const firstResponseRespondedAt =
    ticket?.firstResponse?.respondedAt ||
    ticket?.sla?.firstResponse?.respondedAt ||
    ticket?.firstResponseRespondedAt ||
    ticket?.statusLogs?.find((log: any) => log.newStatus === "IN_PROGRESS")
      ?.createdAt ||
    null;
  const firstResponseDeadline =
    ticket?.firstResponse?.deadline ||
    ticket?.sla?.firstResponse?.deadline ||
    ticket?.firstResponseDeadline ||
    null;
  const firstResponseTargetHours =
    ticket?.firstResponse?.targetHours ||
    ticket?.sla?.firstResponse?.targetHours ||
    ticket?.firstResponseTargetHours ||
    null;

  const getSlaBadgeStyles = (rawStatus?: string | null) => {
    const normalized = rawStatus?.toUpperCase().replace(/\s+/g, "_");

    switch (normalized) {
      case "MET_ON_TIME":
      case "ON_TRACK":
      case "OK":
      case "MET":
      case "COMPLIANT":
        return "bg-green-500/10 text-green-400 border-green-500/30";
      case "WARNING":
      case "AT_RISK":
        return "bg-yellow-500/10 text-yellow-400 border-yellow-500/30";
      case "BREACHED":
      case "BREACHED_ACTIVE":
      case "BREACHED_RESOLVED_LATE":
      case "LATE":
        return "bg-red-500/10 text-red-400 border-red-500/30";
      default:
        return "bg-blue-500/10 text-blue-400 border-blue-500/30";
    }
  };

  const formatSlaLabel = (rawStatus?: string | null) => {
    if (!rawStatus) return "Pending";
    return rawStatus
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const isTechnician = user?.role === "technician" || user?.role === "admin";
  const statusForUi = toUiStatus(status);
  const canProvideFeedback = status === "RESOLVED" || status === "CLOSED";

  return (
    <DashboardLayout>
      {loading ? (
        <div className="min-h-full p-4 sm:p-6 md:p-8 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 text-primary animate-spin" />
            <p className="text-muted-foreground">Loading ticket details...</p>
          </div>
        </div>
      ) : !ticket ? (
        <div className="min-h-full p-4 sm:p-6 md:p-8 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <p className="text-foreground font-semibold mb-2">
              Ticket not found
            </p>
            <p className="text-muted-foreground mb-4">
              The ticket you're looking for doesn't exist or has been deleted.
            </p>
            <button
              onClick={() => navigate("/dashboard")}
              className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors cursor-pointer"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      ) : (
        <div className="min-h-full p-4 sm:p-6 md:p-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-5xl mx-auto space-y-6"
          >
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate("/dashboard")}
                className="p-2 hover:bg-muted rounded-lg transition-colors cursor-pointer"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="flex-1">
                <h1 className="text-2xl sm:text-3xl font-bold text-foreground font-serif">
                  {ticket.ticketNumber || ticket.id}
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                  {ticket.title}
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 space-y-6">
                <div className="bg-card border border-border/50 rounded-xl p-4 sm:p-6 shadow-lg">
                  <h2 className="text-lg font-bold text-foreground mb-4 font-serif">
                    Ticket Information
                  </h2>

                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">
                        Description
                      </label>
                      <p className="text-sm text-foreground mt-1 leading-relaxed">
                        {ticket.description}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                          <Tag className="w-4 h-4" />
                          Category
                        </label>
                        <p className="text-sm text-foreground mt-1">
                          {ticket.category?.name || "N/A"}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          Priority
                        </label>
                        <span
                          className={`inline-block mt-1 px-3 py-1 text-xs font-medium rounded border ${
                            priorityColors[
                              ticket.priority as keyof typeof priorityColors
                            ]
                          }`}
                        >
                          {ticket.priority}
                        </span>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Type
                        </label>
                        <p className="text-sm text-foreground mt-1 capitalize">
                          {ticket.type}
                        </p>
                      </div>
                    </div>

                    {slaDeadline && (
                      <div>
                        <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          SLA Deadline
                        </label>
                        <p className="text-sm text-foreground mt-1">
                          {new Date(slaDeadline).toLocaleString()}
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          Resolution SLA
                        </label>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          <span
                            className={`inline-block px-3 py-1 text-xs font-medium rounded border ${getSlaBadgeStyles(
                              resolutionSlaStatus,
                            )}`}
                          >
                            {formatSlaLabel(resolutionSlaStatus)}
                          </span>
                        </div>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          First Response
                        </label>
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                          <span
                            className={`inline-block px-3 py-1 text-xs font-medium rounded border ${getSlaBadgeStyles(
                              firstResponseStatus,
                            )}`}
                          >
                            {formatSlaLabel(firstResponseStatus)}
                          </span>
                          {firstResponseTargetHours != null && (
                            <span className="text-xs text-muted-foreground">
                              Target: {firstResponseTargetHours}h
                            </span>
                          )}
                        </div>
                        {(firstResponseRespondedAt ||
                          firstResponseDeadline) && (
                          <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                            {firstResponseRespondedAt && (
                              <p>
                                Responded:{" "}
                                {new Date(
                                  firstResponseRespondedAt,
                                ).toLocaleString()}
                              </p>
                            )}
                            {firstResponseDeadline && (
                              <p>
                                Response deadline:{" "}
                                {new Date(
                                  firstResponseDeadline,
                                ).toLocaleString()}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-card border border-border/50 rounded-xl p-4 sm:p-6 shadow-lg">
                  <h2 className="text-lg font-bold text-foreground mb-4 font-serif flex items-center gap-2">
                    <Paperclip className="w-5 h-5" />
                    Attachments ({attachments.length})
                  </h2>

                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`relative border-2 border-dashed rounded-lg p-6 mb-4 transition-all ${
                      isDragging
                        ? "border-primary bg-primary/5"
                        : "border-border/50 hover:border-primary/50 hover:bg-muted/30"
                    }`}
                  >
                    <input
                      type="file"
                      id="file-upload"
                      multiple
                      onChange={(e) => handleFileUpload(e.target.files)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      disabled={uploadingFiles.length > 0}
                    />
                    <div className="text-center pointer-events-none">
                      <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm font-medium text-foreground mb-1">
                        {isDragging
                          ? "Drop files here"
                          : "Drag & drop files or click to browse"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Max 10MB per file • Images, PDFs, Documents
                      </p>
                    </div>
                  </div>

                  {uploadingFiles.length > 0 && (
                    <div className="mb-4 space-y-2">
                      {uploadingFiles.map((fileId) => (
                        <div
                          key={fileId}
                          className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg"
                        >
                          <Loader2 className="w-4 h-4 text-primary animate-spin flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-foreground truncate">
                              Uploading...
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {attachments.length > 0 ? (
                    <div className="space-y-2">
                      {attachments.map((file: any) => (
                        <div
                          key={file.AttachmentID}
                          className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border/30 hover:border-primary/50 transition-colors"
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <FileText className="w-4 h-4 text-primary flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {file.FileName}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {formatFileSize(file.FileSize || 0)} •{" "}
                                {new Date(
                                  file.DateUploaded,
                                ).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              handleDownload(file.AttachmentID, file.FileName)
                            }
                            className="p-2 hover:bg-muted rounded-lg transition-colors flex-shrink-0 cursor-pointer"
                            title="Download file"
                          >
                            <Download className="w-4 h-4 text-primary" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <FileText className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">
                        No attachments yet
                      </p>
                    </div>
                  )}
                </div>

                {isTechnician && (
                  <div className="bg-card border border-border/50 rounded-xl p-4 sm:p-6 shadow-lg">
                    <h2 className="text-lg font-bold text-foreground mb-4 font-serif">
                      Technician Actions
                    </h2>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm font-semibold text-foreground mb-2 block">
                          Update Status
                        </label>
                        <select
                          value={status}
                          onChange={(e) => setStatus(e.target.value)}
                          className="w-full px-4 py-2.5 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm"
                        >
                          {statusOptions.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="text-sm font-semibold text-foreground mb-2 block">
                          Resolution Notes
                        </label>
                        <textarea
                          value={resolutionNote}
                          onChange={(e) => setResolutionNote(e.target.value)}
                          placeholder="Add resolution details, actions taken, or notes..."
                          rows={4}
                          className="w-full px-4 py-3 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm resize-none"
                        />
                      </div>

                      <button
                        onClick={handleStatusUpdate}
                        disabled={isUpdating}
                        className="relative w-full group cursor-pointer"
                      >
                        <div className="absolute inset-0 bg-primary/20 blur-lg group-hover:blur-xl transition-all" />
                        <div className="relative bg-primary hover:bg-primary/90 text-white font-semibold px-6 py-3 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-50">
                          {isUpdating ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              <span>Updating...</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-5 h-5" />
                              <span>Update Status</span>
                            </>
                          )}
                        </div>
                      </button>

                      <button
                        onClick={handleResolveTicket}
                        disabled={isUpdating || !resolutionNote.trim()}
                        className="relative w-full group cursor-pointer"
                      >
                        <div className="absolute inset-0 bg-green-500/20 blur-lg group-hover:blur-xl transition-all" />
                        <div className="relative bg-green-600 hover:bg-green-700 text-white font-semibold px-6 py-3 rounded-lg transition-all flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed">
                          {isUpdating ? (
                            <>
                              <Loader2 className="w-5 h-5 animate-spin" />
                              <span>Resolving...</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-5 h-5" />
                              <span>Mark as Resolved</span>
                            </>
                          )}
                        </div>
                      </button>

                      <div
                        className={`grid ${user?.role === "admin" ? "grid-cols-2" : "grid-cols-1"} gap-3 pt-2 border-t border-border/50`}
                      >
                        <button
                          onClick={() => setShowEscalateDialog(true)}
                          className="flex items-center justify-center gap-2 px-4 py-2.5 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded-lg transition-all text-sm font-medium cursor-pointer"
                        >
                          <AlertTriangle className="w-4 h-4" />
                          <span>Escalate</span>
                        </button>
                        {user?.role === "admin" && (
                          <button
                            onClick={() => setShowReassignDialog(true)}
                            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-500/10 hover:bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-lg transition-all text-sm font-medium cursor-pointer"
                          >
                            <UserPlus className="w-4 h-4" />
                            <span>Reassign</span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-card border border-border/50 rounded-xl p-4 sm:p-6 shadow-lg">
                  <h2 className="text-lg font-bold text-foreground mb-4 font-serif flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Comments & Notes
                  </h2>

                  <div className="space-y-3 mb-4">
                    {comments.length > 0 ? (
                      comments.map((comment) => {
                        const userName =
                          comment.user?.fullName ||
                          comment.UserName ||
                          "Unknown User";
                        const userRole =
                          comment.user?.role || comment.UserRole || "user";
                        const dateCreated =
                          comment.createdAt || comment.DateCreated;
                        const isInternal =
                          comment.isInternal ?? comment.IsInternal ?? false;
                        const commentText = comment.text || comment.Text;
                        const commentId = comment.id || comment.CommentID;

                        return (
                          <div
                            key={commentId}
                            className="p-4 bg-muted/30 rounded-lg border border-border/30"
                          >
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                                  <span className="text-xs font-bold text-primary">
                                    {userName
                                      .split(" ")
                                      .map((n: string) => n[0])
                                      .join("")}
                                  </span>
                                </div>
                                <div>
                                  <p className="text-sm font-semibold text-foreground">
                                    {userName}
                                  </p>
                                  <p className="text-xs text-muted-foreground">
                                    {new Date(dateCreated).toLocaleString()} •{" "}
                                    <span className="capitalize">
                                      {userRole}
                                    </span>
                                  </p>
                                </div>
                              </div>
                              {isInternal && (
                                <span className="px-2 py-0.5 text-xs font-medium bg-yellow-500/10 text-yellow-400 rounded">
                                  Internal
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-foreground leading-relaxed">
                              {commentText}
                            </p>
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-6">
                        <p className="text-muted-foreground text-sm">
                          No comments yet
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <textarea
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      placeholder="Add a comment or note..."
                      rows={3}
                      className="w-full px-4 py-3 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm resize-none"
                    />
                    <button
                      onClick={handleAddComment}
                      disabled={isSubmittingComment}
                      className="flex items-center gap-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white font-medium rounded-lg transition-all text-sm cursor-pointer"
                    >
                      {isSubmittingComment ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Adding...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          <span>Add Comment</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {canProvideFeedback && !isTechnician && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-gradient-to-br from-primary/5 via-card to-card border border-primary/20 rounded-xl p-4 sm:p-6 shadow-lg"
                  >
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Star className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h2 className="text-lg font-bold text-foreground font-serif">
                          Rate This Service
                        </h2>
                        <p className="text-xs text-muted-foreground">
                          Your feedback helps us improve
                        </p>
                      </div>
                    </div>

                    <div className="space-y-5">
                      <div>
                        <label className="text-sm font-medium text-foreground mb-3 block">
                          How satisfied are you with the resolution?
                        </label>
                        <div className="flex gap-2 justify-center sm:justify-start">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <button
                              key={star}
                              onClick={() => setRating(star)}
                              className="transition-all hover:scale-125 active:scale-95 group cursor-pointer"
                              title={
                                [
                                  "Very Poor",
                                  "Poor",
                                  "Average",
                                  "Good",
                                  "Excellent",
                                ][star - 1]
                              }
                            >
                              <Star
                                className={`w-10 h-10 transition-all ${
                                  star <= rating
                                    ? "fill-yellow-400 text-yellow-400 drop-shadow-lg"
                                    : "text-muted-foreground group-hover:text-yellow-400/50"
                                }`}
                              />
                            </button>
                          ))}
                        </div>
                        {rating > 0 && (
                          <p className="text-xs text-center sm:text-left text-muted-foreground mt-2">
                            {
                              [
                                "😞 We're sorry to hear that",
                                "😕 We'll work on improving",
                                "😐 Thank you for your feedback",
                                "😊 Great! We're glad we could help",
                                "🎉 Excellent! Thank you for the 5 stars!",
                              ][rating - 1]
                            }
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="text-sm font-medium text-foreground mb-2 block">
                          Additional Comments{" "}
                          <span className="text-muted-foreground font-normal">
                            (Optional)
                          </span>
                        </label>
                        <textarea
                          value={feedback}
                          onChange={(e) => setFeedback(e.target.value)}
                          placeholder="Share your experience with us..."
                          rows={4}
                          className="w-full px-4 py-3 bg-card border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm resize-none"
                        />
                      </div>

                      <button
                        onClick={handleFeedbackSubmit}
                        disabled={rating === 0 || isSubmittingFeedback}
                        className="w-full px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold rounded-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 cursor-pointer"
                      >
                        {isSubmittingFeedback ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Star className="w-4 h-4" />
                            Submit Feedback
                          </>
                        )}
                      </button>
                    </div>
                  </motion.div>
                )}
              </div>

              <div className="space-y-6">
                <div className="bg-card border border-border/50 rounded-xl p-4 sm:p-6 shadow-lg">
                  <h3 className="text-sm font-bold text-foreground mb-4">
                    Status
                  </h3>
                  <span
                    className={`inline-block px-3 py-1.5 text-sm font-medium rounded ${
                      statusOptions.find(
                        (s) => toUiStatus(s.value) === statusForUi,
                      )?.color
                    }`}
                  >
                    {
                      statusOptions.find(
                        (s) => toUiStatus(s.value) === statusForUi,
                      )?.label
                    }
                  </span>
                </div>

                <div className="bg-card border border-border/50 rounded-xl p-4 sm:p-6 shadow-lg space-y-4">
                  <div>
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-2">
                      <User className="w-3 h-3" />
                      Submitted By
                    </label>
                    <div className="mt-1">
                      <p className="text-sm text-foreground font-medium">
                        {ticket.user?.fullName ||
                          ticket.createdBy?.fullName ||
                          "Unknown"}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {ticket.user?.email || ticket.createdBy?.email || ""}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-2">
                      <User className="w-3 h-3" />
                      Assigned To
                    </label>
                    <div className="mt-1">
                      <p className="text-sm text-foreground font-medium">
                        {ticket.assignee?.fullName ||
                          ticket.assignedTo?.fullName ||
                          ticket.assignedTechnician?.fullName ||
                          (ticket.assignedId ? "Assigned" : "Unassigned")}
                      </p>
                      {(ticket.assignee?.email ||
                        ticket.assignedTo?.email ||
                        ticket.assignedTechnician?.email) && (
                        <p className="text-xs text-muted-foreground">
                          {ticket.assignee?.email ||
                            ticket.assignedTo?.email ||
                            ticket.assignedTechnician?.email}
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      Created
                    </label>
                    <p className="text-sm text-foreground mt-1">
                      {new Date(ticket.createdAt).toLocaleString()}
                    </p>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-muted-foreground flex items-center gap-2">
                      <Clock className="w-3 h-3" />
                      Last Updated
                    </label>
                    <p className="text-sm text-foreground mt-1">
                      {new Date(
                        ticket.updatedAt ||
                          ticket.DateUpdated ||
                          ticket.createdAt,
                      ).toLocaleString()}
                    </p>
                  </div>
                </div>

                <div className="bg-card border border-border/50 rounded-xl p-4 sm:p-6 shadow-lg">
                  <h3 className="text-sm font-bold text-foreground mb-4">
                    Timeline
                  </h3>
                  <div className="space-y-4">
                    {ticket.statusLogs?.map((log: any) => (
                      <div key={log.id} className="flex gap-3">
                        <div className="flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-2" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-foreground">
                            {log.actionType}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {log.user?.fullName || "System"} •{" "}
                            {new Date(log.createdAt).toLocaleString()}
                          </p>
                          {log.oldStatus && log.newStatus && (
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {log.oldStatus} → {log.newStatus}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <WorkflowIndicator
                  title="Ticket Workflow"
                  steps={[
                    {
                      label: "Ticket Created",
                      status: "completed",
                      timestamp: ticket.createdAt,
                      notification: true,
                    },
                    {
                      label: "Auto-Assigned to Technician",
                      status: "completed",
                      timestamp: ticket.statusLogs?.find(
                        (log: any) =>
                          log.actionType === "Assigned to Technician",
                      )?.createdAt,
                      notification: true,
                    },
                    {
                      label:
                        statusForUi === "in-progress"
                          ? "In Progress"
                          : "Pending Assignment",
                      status:
                        statusForUi === "in-progress"
                          ? "in-progress"
                          : statusForUi === "open"
                            ? "pending"
                            : "completed",
                      timestamp: ticket.statusLogs?.find(
                        (log: any) => log.newStatus === "IN_PROGRESS",
                      )?.createdAt,
                      notification: statusForUi !== "open",
                    },
                    {
                      label: "Resolved",
                      status:
                        statusForUi === "resolved" || statusForUi === "closed"
                          ? "completed"
                          : "pending",
                      timestamp: ticket.resolvedAt || undefined,
                      notification:
                        statusForUi === "resolved" || statusForUi === "closed",
                    },
                    {
                      label: "Closed",
                      status:
                        statusForUi === "closed" ? "completed" : "pending",
                      notification: statusForUi === "closed",
                    },
                  ]}
                />
              </div>
            </div>
          </motion.div>

          {showEscalateDialog && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-card border border-border/50 rounded-xl p-6 max-w-md w-full shadow-2xl"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-orange-500/10 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-orange-400" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground">
                    Escalate Ticket
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground mb-6">
                  This will escalate the ticket to a senior technician or
                  manager. Please provide a reason for escalation.
                </p>
                <textarea
                  value={escalationReason}
                  onChange={(e) => setEscalationReason(e.target.value)}
                  placeholder="Reason for escalation..."
                  rows={3}
                  className="w-full px-4 py-3 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm resize-none mb-4"
                />
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowEscalateDialog(false)}
                    className="flex-1 px-4 py-2.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all font-medium cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEscalate}
                    className="flex-1 px-4 py-2.5 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-all font-medium cursor-pointer"
                  >
                    Escalate
                  </button>
                </div>
              </motion.div>
            </div>
          )}

          {showReassignDialog && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="bg-card border border-border/50 rounded-xl p-6 max-w-md w-full shadow-2xl"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-3 bg-blue-500/10 rounded-lg">
                    <UserPlus className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground">
                    Reassign Ticket
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Select a technician to reassign this ticket to:
                </p>
                {loadingTechnicians ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 text-primary animate-spin" />
                    <span className="ml-2 text-sm text-muted-foreground">
                      Loading technicians...
                    </span>
                  </div>
                ) : (
                  <>
                    <select
                      value={selectedTechnicianId}
                      onChange={(e) => setSelectedTechnicianId(e.target.value)}
                      className="w-full px-4 py-3 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm mb-4"
                    >
                      <option value="">Select technician...</option>
                      {technicians.map((tech: any) => (
                        <option
                          key={tech.technician?.id || tech.id}
                          value={tech.technician?.id || tech.id}
                        >
                          {tech.technician?.fullName || tech.fullName} -
                          {tech.activeTickets || 0} active tickets
                        </option>
                      ))}
                    </select>
                    <textarea
                      value={reassignReason}
                      onChange={(e) => setReassignReason(e.target.value)}
                      placeholder="Reason for reassignment (optional)..."
                      rows={2}
                      className="w-full px-4 py-3 bg-muted/50 border border-border/50 rounded-lg focus:ring-2 focus:ring-primary/50 focus:border-primary transition-all text-foreground text-sm resize-none mb-4"
                    />
                  </>
                )}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowReassignDialog(false)}
                    className="flex-1 px-4 py-2.5 bg-muted hover:bg-muted/80 text-foreground rounded-lg transition-all font-medium cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleReassign}
                    className="flex-1 px-4 py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all font-medium cursor-pointer"
                  >
                    Reassign
                  </button>
                </div>
              </motion.div>
            </div>
          )}
        </div>
      )}
    </DashboardLayout>
  );
}
